import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { MaskInput } from 'maska'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

import type { ComputedRef, Ref } from 'vue'
import type { CountryCode } from 'libphonenumber-js'
import type { DropdownOptions, InputOptions } from 'vue-tel-input'
import type { AppInputState } from '../../app-input'
import type {
  AppPhoneCountryData,
  AppPhoneInputProps,
} from '../types/interfaces'

import { applyPhoneMask, nationalPhoneExample, parsePhone, phoneDigits, toPhoneMask, useDayjs } from '@shared/lib/format'
import { countryByTimezone } from '@shared/config'

import {
  APP_PHONE_DEFAULT_COUNTRY,
  APP_PHONE_IGNORED_COUNTRIES,
} from '../types/enums'

interface AppPhoneInputOptions {
  model: Ref<string>
  props: AppPhoneInputProps
  inputId: string
  messageId: string
  onValid: (valid: boolean) => void
}

interface AppPhoneInputReturn {
  national: Ref<string>
  initialCountry: CountryCode
  inputOptions: ComputedRef<InputOptions>
  dropdownOptions: ComputedRef<DropdownOptions>
  resolvedState: ComputedRef<AppInputState | undefined>
  resolvedMessage: ComputedRef<string | undefined>
  changeCountry: (data: AppPhoneCountryData) => void
}

const isSelectable = (code: CountryCode): boolean => !APP_PHONE_IGNORED_COUNTRIES.includes(code)

export const useAppPhoneInput = ({
  model,
  props,
  inputId,
  messageId,
  onValid,
}: AppPhoneInputOptions): AppPhoneInputReturn => {
  const dayjs = useDayjs()

  const parsedModel = model.value ? parsePhone(model.value) : undefined

  const resolveInitialCountry = (): CountryCode => {
    if (parsedModel?.country && isSelectable(parsedModel.country)) return parsedModel.country

    const byTimezone = countryByTimezone[dayjs.tz.guess()] as CountryCode | undefined
    if (byTimezone && isSelectable(byTimezone)) return byTimezone

    const fallback = props.defaultCountry ?? APP_PHONE_DEFAULT_COUNTRY
    return isSelectable(fallback) ? fallback : APP_PHONE_DEFAULT_COUNTRY
  }

  const initialCountry = resolveInitialCountry()

  const country = ref<CountryCode>(initialCountry)

  const example = computed<string>(() => nationalPhoneExample(country.value))

  const mask = computed<string>(() => toPhoneMask(example.value))

  /** Bound programmatically: the `v-maska` directive re-targets `querySelector('input')` on every
   *  update and would grab the dropdown search box. */
  let maskInput: MaskInput | null = null

  onMounted(() => {
    const input = document.getElementById(inputId)
    if (input instanceof HTMLInputElement) maskInput = new MaskInput(input, { mask: mask.value })
  })

  onBeforeUnmount(() => {
    maskInput?.destroy()
    maskInput = null
  })

  watch(mask, (value) => maskInput?.update({ mask: value }))

  const national = ref<string>(
    parsedModel ? applyPhoneMask(String(parsedModel.nationalNumber), mask.value) : '',
  )

  const isValid = ref<boolean>(false)

  const resolvedState = computed<AppInputState | undefined>(() => {
    if (props.state) return props.state
    if (!national.value) return undefined
    return isValid.value ? undefined : 'error'
  })

  const resolvedMessage = computed<string | undefined>(() => {
    if (props.message) return props.message
    if (resolvedState.value === 'error') return props.invalidMsg
    return undefined
  })

  const inputOptions = computed<InputOptions>(() => ({
    id: inputId,
    name: props.name,
    type: 'tel',
    autocomplete: 'tel-national',
    inputmode: 'tel',
    placeholder: props.placeholder || example.value,
    maxlength: mask.value.length,
    required: props.required,
    'aria-describedby': resolvedMessage.value ? messageId : '',
    styleClasses: 'app-phone-input__field',
  }))

  /** Replaces the library defaults wholesale — list every flag. */
  const dropdownOptions = computed<DropdownOptions>(() => ({
    showFlags: true,
    showDialCodeInList: true,
    showDialCodeInSelection: false,
    showSearchBox: true,
    searchBoxPlaceholder: 'Search country',
    ariaLabel: props.label ? `${props.label} country` : 'Phone country',
    disabled: props.disabled,
  }))

  const hasResolvedInitialCountry = ref<boolean>(false)

  const changeCountry = ({ iso2 }: AppPhoneCountryData): void => {
    const isUserSwitch = hasResolvedInitialCountry.value && iso2 !== country.value

    hasResolvedInitialCountry.value = true
    country.value = iso2

    if (isUserSwitch) national.value = ''
  }

  const lastWrite = ref<string>(model.value)

  /** `vue-tel-input`'s `validate` event fires only when validity flips (SPLY-242), so derive the
   *  model from the typed value directly. */
  watch([national, country], ([value]) => {
    const parsed = value ? parsePhoneNumberFromString(phoneDigits(value), country.value) : undefined

    isValid.value = !!parsed?.isValid()
    lastWrite.value = isValid.value && parsed ? parsed.number : ''
    model.value = lastWrite.value

    onValid(isValid.value)
  }, { immediate: true })

  /** Comparing against our own last write tells a parent-side reset apart from our own `''`. */
  watch(model, (value) => {
    if (value === lastWrite.value) return

    lastWrite.value = value

    if (!value) {
      national.value = ''
      return
    }

    const parsed = parsePhone(value)
    if (parsed?.country && isSelectable(parsed.country)) country.value = parsed.country

    national.value = applyPhoneMask(phoneDigits(parsed ? String(parsed.nationalNumber) : value), mask.value)
  })

  return {
    national,
    initialCountry,
    inputOptions,
    dropdownOptions,
    resolvedState,
    resolvedMessage,
    changeCountry,
  }
}
